// WeightGoalsForm.js (Updated)
import { useState, useMemo, useEffect, useCallback } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigationButtons from "./NavigationButtons";
import adviseMeImage from "@assets/images/AdviseMeSidePic.svg";
import AdviseMeHeader from "./AdviseMeHeader";
import ReactModal from "react-modal";
import { useTranslation } from "react-i18next";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  daysOptions,
  timelineOptions,
  durationTypeOptions,
  exerciseTimeOptions,
} from "data/dropdowndData/adviseMeOptions";
import { getAdviseData } from "data/uiData/CardsData/adviseMeCards";

// Import the new components
import EditableInputField from "@components/ui/EditableInputField";
import SimpleSelectField from "@components/ui/SimpleSelectField";
import DynamicSelectField from "@components/ui/DynamicSelectField";
import ExerciseTimeSelect from "@components/ui/ExerciseTimeSelect";
import AdviseMeAuto from "@features/AdviseMe/AdviseMeAuto";
import { manualTarget } from "@utils/defaultImages";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { toast } from "react-toastify";

const WeightGoalsForm = ({
  selectedTags,
  setSelectedTags,
  errors,
  adviceTimer,
  isAdviceLoading,
  setOpen,
  user,
  loading,
  currentStep,
  setCurrentStep,
  validateStep,
  handleSubmit,
  handleInputChange,
  handleSelecChange,
  steps,
  dynamicOptions,
  handleMultiSelectChange,
  autoSelectedTags,
  automaticLoading
}) => {
  const [openDetails, setOpenDetails] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const { t } = useTranslation();

  // Auto-open accordion when advice is received
  useEffect(() => {
    if (selectedTags?.["adviseMeDescription"] && !isAdviceLoading) {
      setAccordionExpanded(true);
    }
  }, [selectedTags?.["adviseMeDescription"], isAdviceLoading]);

  // Calculate daily calorie adjustment
  const calculatedDailyCalorieAdjustment = useMemo(() => {
    const target = Number(selectedTags?.targetDailyCalories);
    const maintenance = Number(selectedTags?.maintenanceCalories);
    if (
      !isNaN(target) &&
      !isNaN(maintenance) &&
      target !== null &&
      maintenance !== null
    ) {
      return Math.round(target - maintenance);
    }
    return 0;
  }, [selectedTags?.targetDailyCalories, selectedTags?.maintenanceCalories]);

  const adviseData = getAdviseData({ selectedTags, t });

  // Helper functions for dynamic options
  const getDynamicDropdownOptions = (key) => {
    const options = dynamicOptions[key] || [];
    if (key === "difficultiesLevel") {
      return options.map((opt) => ({
        value: opt,
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
      }));
    } else {
      return options.map((opt) => ({
        value: opt.id,
        label: opt.name,
      }));
    }
  };

  const getDisplayMap = (key) => {
    const options = dynamicOptions[key] || [];
    const displayMap = {};
    if (key === "difficultiesLevel") {
      options.forEach((opt) => {
        displayMap[opt] = opt.charAt(0).toUpperCase() + opt.slice(1);
      });
    } else {
      options.forEach((opt) => {
        displayMap[opt.id] = opt.name;
      });
    }
    return displayMap;
  };

  const getDisplayName = (key, categoryId = null) => {
    if (!categoryId || !dynamicOptions?.[key]) return categoryId || "";
    if (key === "difficultiesLevel") {
      return categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
    }
    const item = dynamicOptions[key].find((item) => item?.id === categoryId);
    return item ? item.name : categoryId;
  };

  // Handle weight unit change
  const handleWeightUnitChange = (newUnit) => {
    if (selectedTags?.targetWeight) {
      const convertedWeight =
        newUnit === "Lbs"
          ? selectedTags.targetWeight * 2.20462
          : selectedTags.targetWeight / 2.20462;
      setSelectedTags((prev) => ({
        ...prev,
        targetWeight: Math.round(convertedWeight),
        targetWeightUnit: newUnit,
      }));
    } else {
      setSelectedTags((prev) => ({
        ...prev,
        targetWeightUnit: newUnit,
      }));
    }
  };

  // Handle removal functions
  const handleRemoveDay = (dayToRemove) => {
    const updatedDays = (selectedTags?.selectableDays || []).filter(
      (day) => day !== dayToRemove,
    );
    handleMultiSelectChange("selectableDays", updatedDays);
  };

  const handleRemoveWorkoutCategory = (idToRemove) => {
    const updatedCategories = (selectedTags?.workoutsCategories || []).filter(
      (id) => id !== idToRemove,
    );
    handleMultiSelectChange("workoutsCategories", updatedCategories);
  };

  const getAutoFieldValue = useCallback(
    (field) => {
      return autoSelectedTags?.[field] ?? "";
    },
    [autoSelectedTags],
  );

  return (
    <>
      <div className="hidden sm:block mb-5">
        <AdviseMeHeader selectedTags={selectedTags} user={user} t={t} />
      </div>

      <h2 className="text-[20px] md:text-[30px] md:text-center font-[600] text-black-600 md:px-4 mt-2 md:mt-0">
        {t("firstFormSteps.lastStepHeading")}
      </h2>
      <p className="text-[#0575E6] text-[16px] md:text-center  md:px-4">
        {t("firstFormSteps.lastStepSubHeading")}
      </p>

      <AdviseMeAuto
        setOpen={setOpen}
        isAdviceLoading={isAdviceLoading}
        countdown={adviceTimer}
        message={autoSelectedTags?.["adviseMeDescription"]}
        autoSeletctedTags={autoSelectedTags}
        getFieldValue={getAutoFieldValue}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelecChange}
        handleMultiSelectChange={handleMultiSelectChange}
        timelineOptions={timelineOptions}
        durationTypeOptions={durationTypeOptions}
        daysOptions={daysOptions}
        exerciseTimeOptions={exerciseTimeOptions}
        getDynamicDropdownOptions={getDynamicDropdownOptions}
        getDisplayName={getDisplayName}
        handleWeightUnitChange={handleWeightUnitChange}
        getDisplayMap={getDisplayMap}
        handleApplyGoal={handleSubmit}
        autoSubmit={automaticLoading}
      />

      <div className="flex justify-center">
        <div className="w-full mt-3">
          {/* Accordion for Manual Goal Form */}
          <Accordion
            expanded={accordionExpanded}
            onChange={(event, isExpanded) => setAccordionExpanded(isExpanded)}
            sx={{
              "&.MuiAccordion-root": {
                boxShadow: "none",
                border: "0.47px solid #E6E6E6",
                borderRadius: "10px",
              },
              "&.MuiAccordion-root:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: "white",
                  }}
                />
              }
              sx={{
                background: "linear-gradient(360deg, #4CFF7F 0%, #9FF3C8 100%)",
                color: "white",
                fontSize: "20px",
                fontWeight: 600,
                fontFamily: "Lato",
                borderRadius: "10px",
                marginBottom: "8px",
                alignItems: "center",
                "&:hover": {
                  background:
                    "linear-gradient(360deg, #4CFF7F 0%, #9FF3C8 100%)",
                },
              }}
            >
              <div className="flex items-center">
                <img
                  src={manualTarget}
                  alt="Manual Target"
                  className="w-11 h-11 mr-2"
                />
                {t("firstFormSteps.manualGoal")}
              </div>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "20px",
                backgroundColor: "#f9f9f9",
                borderRadius: "10px",
              }}
            >
              {/* Left Column - Input Fields (3 columns on large screens) */}
              <div className="lg:col-span-3 md:px-4">
                {/* Input Fields Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* First row: Target Weight, Timeline */}
                  <div>
                    <EditableInputField
                      field="targetWeight"
                      value={selectedTags?.targetWeight}
                      label={t(`firstFormSteps.targetWeight.label`)}
                      disabled={false}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      selectedTags={selectedTags}
                      handleInputChange={handleInputChange}
                      handleWeightUnitChange={handleWeightUnitChange}
                    />
                  </div>

                  {/* Second row: Goal Body Fat, Goal Waist Circumference */}
                  <div>
                    <EditableInputField
                      field="goalBodyFat"
                      value={selectedTags?.goalBodyFat}
                      label={t("Goal Body Fat")}
                      unit="%"
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <EditableInputField
                      field="goalWaistCircumference"
                      value={selectedTags?.goalWaistCircumference}
                      label={t("Goal Waist Circumference")}
                      unit="cm"
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  {/* Third row: Duration Type, Maintenance Calories */}
                  <div>
                    <EditableInputField
                      field="maintenanceCalories"
                      value={selectedTags?.maintenanceCalories}
                      label={t(`firstFormSteps.maintenanceCalories.label`)}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  {/* Fourth row: Target Daily Calories, Daily Calorie Adjustment */}
                  <div>
                    <EditableInputField
                      field="targetDailyCalories"
                      value={selectedTags?.targetDailyCalories}
                      label={t(`firstFormSteps.targetDailyCalories.label`)}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <EditableInputField
                      field="dailyCalorieAdjustment"
                      value={calculatedDailyCalorieAdjustment}
                      label={t(`preferences.fields.dailyCalorieAdjustment`)}
                      unit="Kcal"
                      disabled={true}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <EditableInputField
                      field="goalSteps"
                      value={selectedTags?.goalSteps}
                      label={t(`preferences.fields.goalSteps`)}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <EditableInputField
                      field="targetActiveTime"
                      value={selectedTags?.targetActiveTime}
                      label={t(`preferences.fields.targetActiveTime`)}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleInputChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <SimpleSelectField
                      field="durationType"
                      value={selectedTags?.durationType}
                      label={t(`firstFormSteps.durationType.label`)}
                      options={durationTypeOptions}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleSelecChange={handleSelecChange}
                    />
                  </div>

                  <div>
                    <SimpleSelectField
                      field="timeline"
                      value={selectedTags?.timeline}
                      label={t(`firstFormSteps.timeline.label`)}
                      options={timelineOptions}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleSelecChange={handleSelecChange}
                    />
                  </div>
                </div>
              </div>

              {/* Exercise Preferences Section */}
              <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  {t("Exercise Preferences")}
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Workout Categories Card */}
                  <div className="col-span-2 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <SportsGymnasticsIcon className="text-blue-500 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {t("Workout Categories")}
                      </span>
                    </div>
                    <ExercisePlanMultiSelectDropdown
                      options={getDynamicDropdownOptions(
                        "workoutsCategories",
                      ).map((opt) => opt.value)}
                      selectedValues={selectedTags?.workoutsCategories || []}
                      onSelect={(values) =>
                        handleMultiSelectChange("workoutsCategories", values)
                      }
                      label={t("Workout Categories")}
                      hasError={errors?.workoutsCategories}
                      placeholder={t("preferences.validation.onlyOne")}
                      isEditable={!isAdviceLoading}
                      isFistForm={true}
                      displayMap={getDisplayMap("workoutsCategories")}
                      fieldKey="workoutsCategories"
                    />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedTags?.workoutsCategories &&
                        selectedTags?.workoutsCategories.length > 0 &&
                        selectedTags?.workoutsCategories.map((categoryId) => (
                          <span
                            key={categoryId}
                            className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800 border border-blue-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-blue-200"
                          >
                            <span>
                              {getDisplayName("workoutsCategories", categoryId)}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveWorkoutCategory(categoryId)
                              }
                              className="ml-1 w-4 h-4 rounded-full text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              aria-label={`Remove workout category`}
                              disabled={isAdviceLoading}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M6 18L18 6M6 6l12 12"
                                ></path>
                              </svg>
                            </button>
                          </span>
                        ))}
                    </div>
                  </div>

                  {/* Equipment Category Card */}
                  <div className="col-span-2 lg:col-span-1 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <FitnessCenterIcon className="text-green-500 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {t("Equipment Category")}
                      </span>
                    </div>
                    <DynamicSelectField
                      field="equipmentCategories"
                      value={selectedTags?.equipmentCategories}
                      label={t("Equipment Category")}
                      dynamicOptions={dynamicOptions}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleSelecChange={handleSelecChange}
                    />
                    <div className="mt-2">
                      {selectedTags?.equipmentCategories && (
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 border border-green-200">
                          {getDisplayName(
                            "equipmentCategories",
                            selectedTags?.equipmentCategories,
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Difficulty Level Card */}
                  <div className="col-span-2 lg:col-span-1 p-4 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <TuneIcon className="text-purple-500 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {t("Difficulty Level")}
                      </span>
                    </div>
                    <DynamicSelectField
                      field="difficultiesLevel"
                      value={selectedTags?.difficultiesLevel}
                      label={t("Difficulty Level")}
                      dynamicOptions={dynamicOptions}
                      errors={errors}
                      isAdviceLoading={isAdviceLoading}
                      handleSelecChange={handleSelecChange}
                    />
                    <div className="mt-2">
                      {selectedTags?.difficultiesLevel && (
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                          {getDisplayName(
                            "difficultiesLevel",
                            selectedTags?.difficultiesLevel,
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Daily Time Preference Card */}
                  <div className="col-span-2 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <AccessTimeIcon className="text-orange-500 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {t("Daily Exercise Time Preference")}
                      </span>
                    </div>
                    <ExerciseTimeSelect
                      value={selectedTags?.dailyExerciseTimePreference}
                      label={t("Daily Exercise Time Preference")}
                      isAdviceLoading={isAdviceLoading}
                      handleSelecChange={handleSelecChange}
                    />
                    {errors["dailyExerciseTimePreference"] && (
                      <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                        {errors["dailyExerciseTimePreference"]}
                      </div>
                    )}
                    <div className="mt-2">
                      {selectedTags?.dailyExerciseTimePreference && (
                        <span className="px-2 py-1 text-xs font-medium rounded-md bg-orange-100 text-orange-800 border border-orange-200">
                          {t(
                            `allPreferencesModule.options.dailyExerciseTimePreference.${selectedTags?.dailyExerciseTimePreference}`,
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Selectable Days Card */}
                  <div className="col-span-full md:col-span-2 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                    <div className="flex items-center mb-2">
                      <CalendarMonthIcon className="text-red-500 mr-2" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        {t("Selectable Days")}
                      </span>
                    </div>
                    <MedicalViewMultipSelectDropdown
                      options={daysOptions?.map((opt) => ({
                        value: opt,
                        label: t(
                          `allPreferencesModule.options.selectableDays.${opt}`,
                        ),
                      }))}
                      selectedValues={selectedTags?.selectableDays || []}
                      onSelect={(selectedOptions) => {
                        const originalValues = selectedOptions.map((opt) =>
                          typeof opt === "object" ? opt.value : opt,
                        );
                        handleMultiSelectChange(
                          "selectableDays",
                          originalValues,
                        );
                      }}
                      isFistForm={true}
                      hasError={errors.selectableDays}
                      label={t("Selectable Days")}
                      placeholder={t("preferences.validation.onlyOne")}
                      isEditable={!isAdviceLoading}
                    />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedTags?.selectableDays &&
                        selectedTags?.selectableDays.length > 0 &&
                        selectedTags?.selectableDays.map((day) => (
                          <span
                            key={day}
                            className="px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-800 border border-red-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-red-200"
                          >
                            <span>
                              {t(
                                `allPreferencesModule.options.selectableDays.${day}`,
                              )}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveDay(day)}
                              className="ml-1 w-4 h-4 rounded-full text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                              aria-label={`Remove ${day}`}
                              disabled={isAdviceLoading}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M6 18L18 6M6 6l12 12"
                                ></path>
                              </svg>
                            </button>
                          </span>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  if (validateStep()) {
                    handleSubmit();
                  } else {
                    toast.error(t("toast.oopsIncorrect"));
                  }
                }}
                className={`px-4 mt-2 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full ${
                  loading || isAdviceLoading
                    ? "opacity-70 pointer-events-none"
                    : ""
                }`}
                disabled={loading}
              >
                {loading && !automaticLoading ? (
                  <ButtonLoader />
                ) : (
                  t("firstFormSteps.navigationButtons.submit")
                )}
              </button>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="mt-6">
        <NavigationButtons
          currentStep={currentStep}
          steps={steps}
          loading={loading}
          isAdviceLoading={isAdviceLoading}
          validateStep={validateStep}
          setCurrentStep={setCurrentStep}
          handleSubmit={handleSubmit}
          selectedTags={selectedTags}
        />
      </div>

      {/* Mobile Details Modal */}
      <ReactModal
        isOpen={openDetails}
        className="rounded-lg w-[100%] max-h-[80%] font-[Lato] shadow-xl border border-gray-200 relative outline-none overflow-y-auto max-w-[90%] bg-white flex justify-center"
        overlayClassName="fixed inset-0 flex items-center justify-center backdrop-blur-sm"
        onRequestClose={() => setOpenDetails(false)}
      >
        <div className="p-[15px]">
          <div className="flex items-center justify-end mb-2">
            <button onClick={() => setOpenDetails(false)}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="12" fill="#0575E6" />
                <path
                  d="M16 8L8 16"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 8L16 16"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="py-4 px-3 md:px-7 rounded-lg bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] min-h-[450px]">
            <div
              id="scrollable-quiz-container"
              className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
            >
              {adviseData.map(({ label, icon, value, tabColor }, index) => (
                <div
                  key={index}
                  className="py-4 px-4 md:py-5 md:px-6 rounded-[16px] bg-white border border-[#E6E6E6] flex flex-col gap-2"
                  style={{
                    boxShadow: "7px 7px 9.5px 0px #0000000D",
                  }}
                >
                  <div className="relative">
                    <div
                      className={`absolute border border-[#0575e6] -left-3 -top-1 rounded-full min-w-12 min-h-12 flex items-center justify-center border`}
                      style={{
                        backgroundColor: tabColor,
                      }}
                    >
                      {icon}
                    </div>
                    <p className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-[14px] lg:text-[16px] font-[600] text-[#fff] py-2 px-8 md:px-10 rounded-full text-center lg:text-start min-w-[180px] md:min-w-[200px] max-w-full">
                      {label}
                    </p>
                  </div>
                  <div className="mt-2 md:mt-0">
                    <p className="-[14px] lg:text-[16px] font-[500] text-[#6C6A6A] font-[Lato] leading-relaxed">
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <style jsx>{`
              .custom-scrollbar::-webkit-scrollbar {
                width: 12px;
              }

              .custom-scrollbar::-webkit-scrollbar-track {
                background-color: #fff !important;
                border-radius: 10px;
                margin: 4px 0;
              }

              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: linear-gradient(
                  150deg,
                  #0771ef 12.89%,
                  #15aafc 85.22%
                );
                border-radius: 10px;
                border: 1px solid rgba(255, 255, 255, 0.2);
                box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
              }
            `}</style>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default WeightGoalsForm;
